import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import useAuth from '../../hooks/useAuth';
import usePost from '../../hooks/usePost';

/**
 * PostForm component for creating new posts
 * @param {Object} props - Component props
 * @param {string} props.communityId - Community ID (optional)
 * @param {Function} props.onSuccess - Callback after successful post creation
 * @returns {JSX.Element} PostForm component
 */
const PostForm = ({ communityId, onSuccess }) => {
  const { user } = useAuth();
  const { createPost } = usePost();
  
  const [content, setContent] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [mediaPreview, setMediaPreview] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const mediaInputRef = useRef(null);

  const handleContentChange = (e) => {
    setContent(e.target.value);
  };

  const handleVisibilityChange = (e) => {
    setVisibility(e.target.value);
  };

  const handleMediaSelect = (e) => {
    const files = Array.from(e.target.files);

    const allowedTypes = process.env.REACT_APP_ALLOWED_FILE_TYPES?.split(',') || [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/webm', 'video/ogg',
      'application/pdf', 'application/msword', 'application/vnd.ms-excel'
    ];
    const maxSize = parseInt(process.env.REACT_APP_MAX_FILE_SIZE, 10) || 10485760; // Default 10MB
    
    const validFiles = files.filter((file) => {
      if (!allowedTypes.includes(file.type)) {
        setError(`File type not supported: ${file.type}`);
        return false;
      }

      if (file.size > maxSize) {
        setError(`File too large: ${(file.size / (1024 * 1024)).toFixed(2)}MB (max ${maxSize / (1024 * 1024)}MB)`);
        return false;
      }
      
      return true;
    });

    setSelectedMedia([...selectedMedia, ...validFiles]);

    const newPreviews = validFiles.map((file) => {
      return {
        file,
        url: URL.createObjectURL(file),
        type: file.type.startsWith('image/') ? 'image' : 
              file.type.startsWith('video/') ? 'video' : 'document',
      };
    });
    
    setMediaPreview([...mediaPreview, ...newPreviews]);

    e.target.value = null;
  };

  const handleRemoveMedia = (index) => {
    if (mediaPreview[index]?.url) {
      URL.revokeObjectURL(mediaPreview[index].url);
    }
    
    const newMedia = [...selectedMedia];
    newMedia.splice(index, 1);
    setSelectedMedia(newMedia);
    
    const newPreviews = [...mediaPreview];
    newPreviews.splice(index, 1);
    setMediaPreview(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!content.trim() && selectedMedia.length === 0) {
      setError('Please enter content or select media');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError('');

      const postData = {
        content: content.trim(),
        visibility,
      };

      if (communityId) {
        postData.communityId = communityId;
      }

      const formData = new FormData();
      formData.append('content', content.trim());
      formData.append('visibility', visibility);
      
      if (communityId) {
        formData.append('communityId', communityId);
      }

      selectedMedia.forEach((file) => {
        formData.append('media', file);
      });

      const handleUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        console.log(`Upload progress: ${percentCompleted}%`);
      };

      await createPost(formData, handleUploadProgress);

      setContent('');
      setSelectedMedia([]);
      setMediaPreview([]);
      setError('');

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error creating post:', error);
      setError(error.response?.data?.message || 'Failed to create post');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-6">
      <div className="flex">
        <Avatar
          src={user?.profilePicture}
          alt={user?.name}
          size="md"
          isOnline={user?.isOnline}
        />
        <div className="ml-3 flex-1">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <textarea
                className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="What's on your mind?"
                rows={3}
                value={content}
                onChange={handleContentChange}
              />
            </div>
            
            {/* Media preview */}
            {mediaPreview.length > 0 && (
              <div className="mb-3">
                <div className={`grid grid-cols-${Math.min(mediaPreview.length, 4)} gap-2`}>
                  {mediaPreview.map((item, index) => (
                    <div key={index} className="relative">
                      {item.type === 'image' && (
                        <div className="relative" style={{ height: '160px', overflow: 'hidden' }}>
                          <img
                            src={item.url}
                            alt="Media preview"
                            className="w-full h-full object-contain"
                          />
                        </div>
                      )}
                      {item.type === 'video' && (
                        <div className="relative" style={{ height: '160px' }}>
                          <video
                            src={item.url}
                            className="w-full h-full object-contain"
                            controls
                          />
                        </div>
                      )}
                      {item.type === 'document' && (
                        <div className="w-full h-32 bg-gray-100 rounded flex items-center justify-center">
                          <div className="text-center">
                            <svg
                              className="h-8 w-8 mx-auto text-gray-400"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                              />
                            </svg>
                            <span className="text-xs text-gray-500 truncate block max-w-full px-2">
                              {item.file.name}
                            </span>
                          </div>
                        </div>
                      )}
                      <button
                        type="button"
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600"
                        onClick={() => handleRemoveMedia(index)}
                      >
                        <svg
                          className="h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Error message */}
            {error && (
              <div className="mb-3 text-red-500 text-sm">{error}</div>
            )}
            
            <div className="flex items-center justify-between border-t border-gray-200 pt-3">
              <div className="flex items-center space-x-2">
                {/* Media upload button */}
                <button
                  type="button"
                  className="p-2 text-gray-500 hover:text-blue-600 rounded-full hover:bg-gray-100"
                  onClick={() => mediaInputRef.current.click()}
                >
                  <svg
                    className="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </button>
                <input
                  type="file"
                  ref={mediaInputRef}
                  className="hidden"
                  onChange={handleMediaSelect}
                  multiple
                  accept={process.env.REACT_APP_ALLOWED_FILE_TYPES || "image/*,video/*,application/pdf"}
                />
                
                {/* Visibility selector */}
                <select
                  className="p-2 border border-gray-300 rounded text-sm"
                  value={visibility}
                  onChange={handleVisibilityChange}
                >
                  <option value="public">Public</option>
                  <option value="followers">Followers</option>
                  {communityId && <option value="community">Community Only</option>}
                  <option value="private">Private</option>
                </select>
              </div>
              
              {/* Submit button */}
              <Button
                type="submit"
                variant="primary"
                size="sm"
                isLoading={isSubmitting}
                isDisabled={isSubmitting || (!content.trim() && selectedMedia.length === 0)}
              >
                Post
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

PostForm.propTypes = {
  communityId: PropTypes.string,
  onSuccess: PropTypes.func,
};

export default PostForm;